/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  Image,
  ImageSelectorImage,
  ImageSelectorProps,
  RegenerateScreenshotMetadataFieldInput,
  Screenshot
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { useCallback, useMemo, useState } from "react"

import ImageSelectorAndCarouselView from "./ImageSelectorAndCarousel"

export interface Props {
  images?: (Image | Screenshot)[]
  isEditMode?: boolean
  showScreenshotMetadataInputs?: boolean
  onInferImageCredit?: (url: string) => Promise<string | undefined>
  onRegenerateScreenshotMetadataField?: (
    input: RegenerateScreenshotMetadataFieldInput
  ) => Promise<string | undefined>
  onSetSelectedImages: (selectedImages: ImageSelectorImage[]) => void
  selectedImages: ImageSelectorImage[]
  mediaLabel?: string
}

export interface ComponentProps extends Omit<Props, "onSetSelectedImages"> {
  imageSelectorProps: ImageSelectorProps
  selectedImages: ImageSelectorImage[]
  mediaLabel?: string
}

const usePrepareComponent = ({
  onSetSelectedImages,
  ...props
}: Props): ComponentProps => {
  const [isImageSelectorModalOpen, setIsImageSelectorModalOpen] =
    useState(false)

  const isScreenshotSelector = props.mediaLabel?.toLowerCase() === "screenshots"

  const selectedImagesForSelector = useMemo(
    () =>
      props.selectedImages.map((item) => ({
        ...item,
        isSelected: true
      })),
    [props.selectedImages]
  )

  /**
   * onRegenerateScreenshotMetadataField
   *
   * @description
   * Regenerates one screenshot metadata field and writes the returned value
   * back into the currently selected image list.
   * @param {RegenerateScreenshotMetadataFieldInput} input - Metadata field
   * regenerate payload for the active screenshot.
   * @returns {Promise<string | undefined>} Regenerated field value, or
   * undefined when regeneration is unavailable/empty.
   *
   */
  const onRegenerateScreenshotMetadataField = useCallback(
    async (
      input: RegenerateScreenshotMetadataFieldInput
    ): Promise<string | undefined> => {
      if (!props.onRegenerateScreenshotMetadataField) {
        return undefined
      }

      const regeneratedValue =
        await props.onRegenerateScreenshotMetadataField(input)

      if (!regeneratedValue?.trim()) {
        return undefined
      }

      const normalizedRegeneratedValue = regeneratedValue.trim()

      onSetSelectedImages(
        props.selectedImages.map((image) => {
          const isIdMatch = input.id ? image.id === input.id : false
          const isContentIdMatch = input.contentId
            ? image.contentId?.toString() === input.contentId.toString()
            : false
          const isOriginalUrlMatch = image.originalUrl === input.originalUrl

          return isIdMatch || isContentIdMatch || isOriginalUrlMatch
            ? {
                ...image,
                [input.field]: normalizedRegeneratedValue
              }
            : image
        })
      )

      return normalizedRegeneratedValue
    },
    [
      onSetSelectedImages,
      props.onRegenerateScreenshotMetadataField,
      props.selectedImages
    ]
  )

  const imageSelectorProps: ImageSelectorProps = useMemo(
    () => ({
      images: selectedImagesForSelector,
      isMultiSelect: true,
      modal: {
        isOpen: isImageSelectorModalOpen,
        name: props.mediaLabel
          ? `${props.mediaLabel} Selector`
          : "Image Selector",
        onClose: () => {
          setIsImageSelectorModalOpen(false)
        }
      },
      onConfirm: (newSelectedImages) => {
        onSetSelectedImages(newSelectedImages ?? [])
        setIsImageSelectorModalOpen(false)
      },
      onInferImageCredit: props.onInferImageCredit,
      onRegenerateScreenshotMetadataField,
      onSelectClick: () => {
        setIsImageSelectorModalOpen(true)
      },
      showCreditInput: isScreenshotSelector,
      showScreenshotMetadataInputs: isScreenshotSelector
        ? Boolean(props.showScreenshotMetadataInputs)
        : false
    }),
    [
      isImageSelectorModalOpen,
      isScreenshotSelector,
      onRegenerateScreenshotMetadataField,
      onSetSelectedImages,
      props.onInferImageCredit,
      props.mediaLabel,
      props.showScreenshotMetadataInputs,
      selectedImagesForSelector
    ]
  )

  return {
    ...props,
    mediaLabel: props.mediaLabel,
    imageSelectorProps
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  ImageSelectorAndCarouselView
)
